// src/utils/ticketQuery.ts
//
// Pure query helpers for the ticket views: the page size and the age-bucket →
// cutoff-timestamp translation. Kept separate from the service so the boundary
// math is unit-testable without a Supabase mock, and so 4.2+ can reuse the same
// constants. No date library (dateUtils.ts covers display); plain Date math.

import type { YakTicketAgeBucket } from '../types/yak'

// Fixed page size for all three views (25/page — pagination, never infinite
// scroll). Exported so the service, the hook, and the pagination UI agree.
export const TICKETS_PAGE_SIZE = 25

// Age filter buckets → whole-day offsets. "Older than N days" means
// created_at < now - N days. Tunable here (the spine names "age" without
// values); buckets beat a raw date input for a click-first audience.
const AGE_BUCKET_DAYS: Record<YakTicketAgeBucket, number> = {
  '1d': 1,
  '3d': 3,
  '7d': 7,
}

// Returns the ISO cutoff for an age bucket: rows with created_at strictly
// before this are "older than N days". null bucket (or unknown) = any age
// (no cutoff, so the service applies no age predicate).
export function ageCutoffIso(
  bucket: YakTicketAgeBucket | null,
  now: Date
): string | null {
  if (!bucket) return null
  const days = AGE_BUCKET_DAYS[bucket]
  if (days == null) return null
  const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  return cutoff.toISOString()
}

// Story 6.3: neutralize SQL LIKE metacharacters in operator-typed text before it
// reaches PostgREST's `ilike`. A pattern with no `%`/`_` is an exact match, which
// is what the reference-code filter means (FR-43); an unescaped `%` a user pasted
// would silently widen it into a wildcard scan. Backslash first, so escaping the
// escape character can't double-process the ones after it.
export function escapeLike(value: string): string {
  // `*` is escaped alongside `%` and `_` because PostgREST accepts it as the
  // URL-safe ALIAS for `%` in like/ilike and substitutes it server-side before
  // the pattern reaches SQL. Without it the "exact match, no wildcards" contract
  // is defeated by one keystroke: `Acme-2026*` becomes a prefix scan and a bare
  // `*` returns the whole page presented as exact matches. Reference codes
  // contain none of these characters, so escaping them all means a typo returns
  // zero rows — the correct outcome (Story 6.3 review).
  return value.replace(/\\/g, '\\\\').replace(/[%_*]/g, m => `\\${m}`)
}
